/**
 * Mural Configurator v3.2 — absolute zoom model (baseWidthM anchor)
 * 
 * A Shopify-integrated mural configuration tool that allows customers to:
 * - Define their wall dimensions (width snaps to lane multiples)
 * - Visually position a mural image within their wall frame
 * - Zoom and pan the mural (per-product permissions)
 * - Select materials with per-material pricing
 * - Add configured murals to the Shopify cart with full metadata
 * 
 * Usage:
 *   The Liquid theme app block renders a container element with data attributes
 *   populated from product metafields. This module reads those attributes and
 *   initializes the configurator.
 * 
 *   <div id="mural-configurator" data-config='{ ... }'></div>
 *   <script src="mural-configurator.js"></script>
 *   <script>MuralConfigurator.init('mural-configurator');</script>
 * 
 * Dependencies: None (vanilla JS)
 * Bundle size target: < 30KB gzipped
 */

(function (root) {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 1: CONSTANTS & DEFAULTS
  // ═══════════════════════════════════════════════════════════════════════════

  const VERSION = '3.2';
  const EPS = 1e-9;

  /** Default values when metafields are not set */
  const DEFAULTS = {
    minHeight: 2.0,       // metres
    maxHeight: 3.5,
    minWidth: 1.47,       // metres (3 lanes of 0.49)
    maxWidth: 10.0,
    zoomMin: 0.01,
    zoomMax: 1.0,
    zoomStep: null,       // null = smooth
    heightStep: 0.01,     // metres
    offsetStep: null,     // null = smooth
    muralType: 'single',  // 'single' | 'seamless' | 'fixed'
    lanesType: 'strips',  // 'strips' | 'continuous'
    minArea: null,        // null = no minimum
    maxArea: null,        // null = no maximum
    allowZoom: false,
    allowPanX: false,
    allowPanY: false,
    allowVerticalStack: false,
  };

  /** Bilingual UI strings */
  const STRINGS = {
    nl: {
      configureButton: 'Configureer je mural',
      configureAnother: 'Nog een muur configureren',
      modalTitle: (name) => `${name} configureren`,
      step1Title: 'Wat is de maat van je muur?',
      step1Subtitle: 'Voer de breedte en hoogte in centimeters in',
      widthLabel: 'Breedte',
      heightLabel: 'Hoogte',
      unitCm: 'cm',
      snappedInfo: (cm, lanes, laneCm) => `Breedte wordt ${cm} cm (${lanes} banen van ${laneCm} cm)`,
      heightWarning: (min, max) => `Hoogte moet tussen ${min} en ${max} cm liggen`,
      widthWarning: (max) => `Maximale breedte is ${max} cm`,
      widthTooSmall: (min) => `Minimale breedte is ${min} cm (1 baan)`,
      continueBtn: 'Verder',
      zoomLabel: 'Zoom',
      zoomSizeWarning: 'Deze muurmaat overschrijdt het maximale zoombereik voor deze mural.',
      addToCart: 'In winkelwagen',
      orderNow: 'Bestel nu',
      materialLabel: 'Materiaal',
      pricePerM2: '/m²',
      minAreaWarning: (min, current) => `Minimale bestelling: ${min} m² — huidige maat: ${current} m²`,
      maxAreaWarning: (max, current) => `Maximale bestelling: ${max} m² — huidige maat: ${current} m²`,
      areaInfo: (area) => `Oppervlak: ${area} m²`,
      savedToast: 'Je configuratie is opgeslagen',
      addedToast: 'Mural toegevoegd aan winkelwagen',
      wallLabel: (i) => `Muur ${i}`,
      editLink: 'Bewerk',
      removeTitle: 'Verwijderen',
      goToCart: 'Naar winkelwagen',
      lanesLabel: (n) => `${n} banen`,
      priceInfoBreakdown: (w, h, area, matName, priceM2) =>
        `${w} × ${h} cm\n${area} m² × ${priceM2}/m²\nMateriaal: ${matName}`,
      limitedOptions: 'Sommige opties zijn momenteel beperkt beschikbaar.',
    },
    en: {
      configureButton: 'Configure your mural',
      configureAnother: 'Configure another wall',
      modalTitle: (name) => `Configure ${name}`,
      step1Title: 'What size is your wall?',
      step1Subtitle: 'Enter the width and height in centimetres',
      widthLabel: 'Width',
      heightLabel: 'Height',
      unitCm: 'cm',
      snappedInfo: (cm, lanes, laneCm) => `Width will be ${cm} cm (${lanes} strips of ${laneCm} cm)`,
      heightWarning: (min, max) => `Height must be between ${min} and ${max} cm`,
      widthWarning: (max) => `Maximum width is ${max} cm`,
      widthTooSmall: (min) => `Minimum width is ${min} cm (1 strip)`,
      continueBtn: 'Continue',
      zoomLabel: 'Zoom',
      zoomSizeWarning: 'This wall size exceeds the maximum configurable size for this mural at the allowed zoom range.',
      addToCart: 'Add to cart',
      orderNow: 'Order now',
      materialLabel: 'Material',
      pricePerM2: '/m²',
      minAreaWarning: (min, current) => `Minimum order: ${min} m² — current size: ${current} m²`,
      maxAreaWarning: (max, current) => `Maximum order: ${max} m² — current size: ${current} m²`,
      areaInfo: (area) => `Area: ${area} m²`,
      savedToast: 'Your configuration has been saved',
      addedToast: 'Mural added to cart',
      wallLabel: (i) => `Wall ${i}`,
      editLink: 'Edit',
      removeTitle: 'Remove',
      goToCart: 'Go to cart',
      lanesLabel: (n) => `${n} strips`,
      priceInfoBreakdown: (w, h, area, matName, priceM2) =>
        `${w} × ${h} cm\n${area} m² × ${priceM2}/m²\nMaterial: ${matName}`,
      limitedOptions: 'Some options may be limited right now.',
    }
  };

  /** Canvas rendering constants */
  const CANVAS = {
    padding: 40,          // px around the wall frame
    maxDpr: 2,            // cap device pixel ratio
    dimOverlay: 'rgba(20, 18, 15, 0.6)',
    wallBorder: 'rgba(255, 255, 255, 0.4)',
    wallBorderWidth: 1.5,
    laneDash: [6, 4],
    laneColor: 'rgba(255, 255, 255, 0.35)',
    laneWidth: 1,
    labelColor: 'rgba(255, 255, 255, 0.7)',
    labelFontSize: 12,
    bgColor: '#2a2722',
  };


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 2: UTILITY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Format a price according to locale.
   * Dutch: €1.234,56  |  English: €1,234.56
   */
  function formatPrice(amount, locale) {
    if (locale === 'nl') {
      return '€' + amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    return '€' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Snap a width in cm UP to the nearest lane multiple.
   * Returns { lanes, widthCm, widthM }
   */
  function snapWidthUp(inputCm, laneWidthM, maxWidthM) {
    const laneCm = Math.round(laneWidthM * 100);
    const lanes = Math.max(1, Math.ceil(inputCm / laneCm));
    const maxLanes = Math.floor(maxWidthM / laneWidthM);
    const clampedLanes = Math.min(lanes, maxLanes);
    return {
      lanes: clampedLanes,
      widthCm: clampedLanes * laneCm,
      widthM: clampedLanes * laneWidthM,
    };
  }

  /**
   * Clamp a value between min and max.
   */
  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Snap a value to the nearest step increment.
   * If step is null, returns the value unchanged.
   */
  function snapToStep(value, step, min) {
    if (!step) return value;
    return min + Math.round((value - min) / step) * step;
  }

  /**
   * Create a DOM element with attributes and children.
   */
  function el(tag, attrs, ...children) {
    const elem = document.createElement(tag);
    if (attrs) {
      for (const [key, val] of Object.entries(attrs)) {
        if (key === 'className') elem.className = val;
        else if (key === 'style' && typeof val === 'object') Object.assign(elem.style, val);
        else if (key.startsWith('on') && typeof val === 'function') {
          elem.addEventListener(key.slice(2).toLowerCase(), val);
        }
        else elem.setAttribute(key, val);
      }
    }
    for (const child of children) {
      if (typeof child === 'string') elem.appendChild(document.createTextNode(child));
      else if (child) elem.appendChild(child);
    }
    return elem;
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 3: CONFIGURATION PARSER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Parse the data-config JSON attribute from the container element.
   * Merges with defaults and validates.
   * 
   * Expected shape of the JSON (populated from Liquid / metafields):
   * {
   *   productName: "Tropical Monstera",
   *   productId: 123456,
   *   variantId: 789012,               // default variant (or first material variant)
   *   productPrice: 34.95,             // price per m² (single-material fallback)
   *   productImage: "https://...",     // fallback product image URL
   *   previewImage: "https://...",     // optimized preview image URL (preferred)
   *   laneWidth: 0.48,                // metres
   *   defaultWidth: 3.36,             // metres
   *   defaultHeight: 2.70,            // metres
   *   muralType: "seamless",
   *   lanesType: "strips",
   *   customizable: true,
   *   allowZoom: true,
   *   zoomMin: 0.5,
   *   zoomMax: 2.0,
   *   zoomStep: 0.1,
   *   allowPanX: true,
   *   allowPanY: true,
   *   minHeight: 2.0,
   *   maxHeight: 3.5,
   *   maxWidth: 10.0,
   *   heightStep: 0.01,
   *   offsetStep: null,
   *   minArea: 3.0,
   *   materials: [
   *     { name: "Vliesbehang", key: "vlies", price_m2: 34.95, variantId: 789012, desc: "..." },
   *     { name: "Vinyl", key: "vinyl", price_m2: 42.50, variantId: 789013, desc: "..." }
   *   ],
   *   defaultMaterial: "vlies",
   *   apiEnabled: false,
   *   apiProvider: null,
   *   apiProductId: null,
   *   locale: "nl",                   // "nl" or "en"
   *   cartUrl: "/cart",
   *   addToCartUrl: "/cart/add.js",
   * }
   */
  function parseConfig(container) {
    const raw = container.getAttribute('data-config');
    if (!raw) {
      console.error('[MuralConfigurator] No data-config attribute found on container.');
      return null;
    }

    let json;
    try {
      json = JSON.parse(raw);
    } catch (e) {
      console.error('[MuralConfigurator] Invalid JSON in data-config:', e);
      return null;
    }

    // Merge with defaults
    const config = {
      productName: json.productName || 'Mural',
      productId: json.productId,
      variantId: json.variantId,
      productPrice: parseFloat(json.productPrice) || 0,
      productImage: json.productImage || '',
      previewImage: json.previewImage || json.productImage || '',
      laneWidth: parseFloat(json.laneWidth) || 0.48,
      defaultWidth: parseFloat(json.defaultWidth) || 3.36,
      defaultHeight: parseFloat(json.defaultHeight) || 2.70,
      muralType: json.muralType || DEFAULTS.muralType,
      lanesType: json.lanesType || DEFAULTS.lanesType,
      customizable: json.customizable !== false,
      allowZoom: json.allowZoom === true,
      zoomMin: parseFloat(json.zoomMin) || DEFAULTS.zoomMin,
      zoomMax: parseFloat(json.zoomMax) || DEFAULTS.zoomMax,
      zoomStep: json.zoomStep ? parseFloat(json.zoomStep) : DEFAULTS.zoomStep,
      allowPanX: json.allowPanX === true,
      allowPanY: json.allowPanY === true,
      allowVerticalStack: json.allowVerticalStack === true,
      minHeight: parseFloat(json.minHeight) || DEFAULTS.minHeight,
      maxHeight: parseFloat(json.maxHeight) || DEFAULTS.maxHeight,
      minWidth: parseFloat(json.minWidth) || DEFAULTS.minWidth,
      maxWidth: parseFloat(json.maxWidth) || DEFAULTS.maxWidth,
      heightStep: json.heightStep ? parseFloat(json.heightStep) : DEFAULTS.heightStep,
      offsetStep: json.offsetStep ? parseFloat(json.offsetStep) : DEFAULTS.offsetStep,
      minArea: json.minArea ? parseFloat(json.minArea) : DEFAULTS.minArea,
      maxArea: json.maxArea ? parseFloat(json.maxArea) : DEFAULTS.maxArea,
      materials: Array.isArray(json.materials) ? json.materials : [],
      defaultMaterial: json.defaultMaterial || null,
      apiEnabled: json.apiEnabled === true,
      apiProvider: json.apiProvider || null,
      apiProductId: json.apiProductId || null,
      locale: json.locale === 'en' ? 'en' : 'nl',
      cartUrl: json.cartUrl || '/cart',
      addToCartUrl: json.addToCartUrl || '/cart/add.js',
      baseWidth: parseFloat(json.baseWidth) || 0,   // 0 = use defaultWidth as fallback
    };

    // Validation
    if (config.zoomMin > config.zoomMax) {
      console.warn('[MuralConfigurator] zoomMin > zoomMax, swapping.');
      [config.zoomMin, config.zoomMax] = [config.zoomMax, config.zoomMin];
    }

    // If no materials defined, create a single implicit material from product price
    if (config.materials.length === 0 && config.productPrice > 0) {
      config.materials = [{
        name: config.locale === 'nl' ? 'Standaard' : 'Standard',
        key: 'default',
        price_m2: config.productPrice,
        variantId: config.variantId,
      }];
    }

    // Set default material
    if (config.defaultMaterial) {
      config._defaultMaterialIndex = config.materials.findIndex(m => m.key === config.defaultMaterial);
      if (config._defaultMaterialIndex < 0) config._defaultMaterialIndex = 0;
    } else {
      config._defaultMaterialIndex = 0;
    }

    return config;
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 4: STATE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a fresh state object from config.
   */
  function createState(config) {
    const snap = snapWidthUp(
      Math.round(config.defaultWidth * 100),
      config.laneWidth,
      config.maxWidth
    );
    return {
      widthM: snap.widthM,
      heightM: config.defaultHeight,
      lanes: snap.lanes,
      zoom: 1.0,
      baseWidthM: config.baseWidth || config.defaultWidth,  // fixed reference, UNSNAPPED
      offsetX: 0,
      offsetY: 0,
      materialIndex: config._defaultMaterialIndex,
      step: 1,        // 1 = wall size, 2 = configurator
    };
  }

  /**
   * Deep-clone a state object.
   */
  function cloneState(state) {
    return { ...state };
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 5: CANVAS RENDERER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * CanvasRenderer handles all drawing operations for the mural preview.
   * It is stateless — given a state and config, it draws the correct frame.
   */
  class CanvasRenderer {
    constructor(canvasElement, areaElement) {
      this.canvas = canvasElement;
      this.area = areaElement;
      this.ctx = canvasElement.getContext('2d');
      this.image = null;
      this.imageLoaded = false;
    }

    /**
     * Load the mural preview image.
     * Returns a Promise that resolves when the image is ready.
     */
    loadImage(url) {
      return new Promise((resolve, reject) => {
        if (this.image && this.image.src === url && this.imageLoaded) {
          resolve(this.image);
          return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.image = img;
          this.imageLoaded = true;
          resolve(img);
        };
        img.onerror = (e) => {
          console.error('[MuralConfigurator] Failed to load preview image:', url);
          reject(e);
        };
        img.src = url;
      });
    }

    /**
     * Calculate wall and mural dimensions in canvas pixels.
     * Returns null if dimensions are not yet available.
     */
    calcDimensions(state) {
      const areaW = this.area.clientWidth;
      const areaH = this.area.clientHeight;
      if (areaW === 0 || areaH === 0 || !this.imageLoaded) return null;

      const wallAspect = state.widthM / state.heightM;

      const maxW = areaW - CANVAS.padding * 2;
      const maxH = areaH - CANVAS.padding * 2;

      let wallPxW, wallPxH;
      if (maxW / maxH > wallAspect) {
        wallPxH = maxH;
        wallPxW = wallPxH * wallAspect;
      } else {
        wallPxW = maxW;
        wallPxH = wallPxW / wallAspect;
      }

      // Absolute zoom model: zoom is anchored to a fixed baseWidthM.
      // zoom = 1 → mural width = baseWidthM (natural/designed size).
      // muralWidthM = baseWidthM × zoom, independent of wall width.
      const pxPerM = wallPxW / state.widthM;
      const muralPxW = state.baseWidthM * state.zoom * pxPerM;
      const muralPxH = (this.image.height / this.image.width) * muralPxW;

      return {
        areaW, areaH,
        wallPxW, wallPxH,
        wallX: (areaW - wallPxW) / 2,
        wallY: (areaH - wallPxH) / 2,
        muralPxW, muralPxH,
      };
    }

    /**
     * Render the full canvas: mural image, dim overlay, wall border,
     * lane dividers, and dimension labels.
     */
    render(state, config) {
      const dims = this.calcDimensions(state);
      if (!dims) return;

      const { areaW, areaH, wallPxW, wallPxH, wallX, wallY, muralPxW, muralPxH } = dims;
      const dpr = Math.min(window.devicePixelRatio || 1, CANVAS.maxDpr);

      // Size canvas
      this.canvas.width = areaW * dpr;
      this.canvas.height = areaH * dpr;
      this.canvas.style.width = areaW + 'px';
      this.canvas.style.height = areaH + 'px';
      this.canvas.style.position = 'absolute';
      this.canvas.style.left = '0';
      this.canvas.style.top = '0';

      const ctx = this.ctx;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Background
      ctx.fillStyle = CANVAS.bgColor;
      ctx.fillRect(0, 0, areaW, areaH);

      // Draw mural image(s)
      this._drawMuralImage(ctx, state, config, dims);

      // Dim overlay outside wall
      this._drawDimOverlay(ctx, areaW, areaH, wallX, wallY, wallPxW, wallPxH);

      // Wall border
      ctx.strokeStyle = CANVAS.wallBorder;
      ctx.lineWidth = CANVAS.wallBorderWidth;
      ctx.strokeRect(wallX, wallY, wallPxW, wallPxH);

      // Lane dividers
      if (config.lanesType === 'strips' && state.lanes > 1) {
        this._drawLaneDividers(ctx, wallX, wallY, wallPxW, wallPxH, state.lanes);
      }

      // Dimension labels
      this._drawLabels(ctx, state, wallX, wallY, wallPxW, wallPxH);
    }

    /** @private Draw the mural image, tiling when needed */
    _drawMuralImage(ctx, state, config, dims) {
      const { wallX, wallY, wallPxW, wallPxH, muralPxW, muralPxH, areaW, areaH } = dims;

      // Tile horizontally: explicit seamless OR mural narrower than wall (auto-fill)
      const tileH = config.muralType === 'seamless' || muralPxW < wallPxW - 1;
      // Tile vertically: explicit verticalStack OR mural shorter than wall (auto-fill)
      const tileV = config.allowVerticalStack || muralPxH < wallPxH - 1;

      if (tileH || tileV) {
        // Tiling mode: repeat horizontally (seamless) and/or vertically (verticalStack)
        const baseX = tileH
          ? wallX + state.offsetX
          : wallX + state.offsetX + (wallPxW - muralPxW) / 2;
        const baseY = tileV
          ? wallY + state.offsetY
          : wallY + state.offsetY + (wallPxH - muralPxH) / 2;

        const startCol = tileH ? Math.floor((0 - baseX) / muralPxW) - 1 : 0;
        const endCol   = tileH ? Math.ceil((areaW - baseX) / muralPxW) + 1 : 0;
        const startRow = tileV ? Math.floor((0 - baseY) / muralPxH) - 1 : 0;
        const endRow   = tileV ? Math.ceil((areaH - baseY) / muralPxH) + 1 : 0;

        for (let r = startRow; r <= endRow; r++) {
          for (let c = startCol; c <= endCol; c++) {
            ctx.drawImage(this.image, baseX + c * muralPxW, baseY + r * muralPxH, muralPxW, muralPxH);
          }
        }
      } else {
        // Single image, centered
        const imgX = wallX + state.offsetX + (wallPxW - muralPxW) / 2;
        const imgY = wallY + state.offsetY + (wallPxH - muralPxH) / 2;
        ctx.drawImage(this.image, imgX, imgY, muralPxW, muralPxH);
      }
    }

    /** @private Draw the semi-transparent overlay outside the wall area */
    _drawDimOverlay(ctx, areaW, areaH, wallX, wallY, wallPxW, wallPxH) {
      ctx.fillStyle = CANVAS.dimOverlay;
      ctx.fillRect(0, 0, areaW, wallY);                                     // top
      ctx.fillRect(0, wallY + wallPxH, areaW, areaH - wallY - wallPxH);     // bottom
      ctx.fillRect(0, wallY, wallX, wallPxH);                               // left
      ctx.fillRect(wallX + wallPxW, wallY, areaW - wallX - wallPxW, wallPxH); // right
    }

    /** @private Draw dashed lane divider lines */
    _drawLaneDividers(ctx, wallX, wallY, wallPxW, wallPxH, lanes) {
      const lanePxW = wallPxW / lanes;
      ctx.setLineDash(CANVAS.laneDash);
      ctx.strokeStyle = CANVAS.laneColor;
      ctx.lineWidth = CANVAS.laneWidth;
      for (let i = 1; i < lanes; i++) {
        const x = wallX + i * lanePxW;
        ctx.beginPath();
        ctx.moveTo(x, wallY);
        ctx.lineTo(x, wallY + wallPxH);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    /** @private Draw dimension labels on the wall edges */
    _drawLabels(ctx, state, wallX, wallY, wallPxW, wallPxH) {
      ctx.fillStyle = CANVAS.labelColor;
      ctx.font = `500 ${CANVAS.labelFontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(
        `${Math.round(state.widthM * 100)} cm`,
        wallX + wallPxW / 2,
        wallY - 10
      );
      ctx.save();
      ctx.translate(wallX - 10, wallY + wallPxH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(`${Math.round(state.heightM * 100)} cm`, 0, 0);
      ctx.restore();
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 6: BOUNDARY ENFORCEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * BoundaryEnforcer ensures the mural always fully covers the wall.
   * No empty gaps allowed.
   *
   * ABSOLUTE ZOOM MODEL:
   *   zoom is anchored to a fixed baseWidthM (product's designed width).
   *   zoom = 1 → mural width = baseWidthM (natural/designed size).
   *
   *   muralWidthM  = baseWidthM × zoom
   *   muralHeightM = baseWidthM × zoom / imgAspect
   *   muralPxW     = wallPxW × (baseWidthM / widthM) × zoom
   *   muralPxH     = (imgH / imgW) × muralPxW
   *
   * Key advantage: no zoom compensation needed when changing wall width.
   * baseWidthM is a fixed anchor, independent of the wall dimensions.
   */
  const BoundaryEnforcer = {

    /**
     * Pure coverage minimum: the smallest zoom that ensures the mural
     * fully covers the wall. Does NOT incorporate config.zoomMin.
     * Used by enforceZoom for allowZoom=false (auto-fill).
     */
    getCoverageMin(config, state, image) {
      if (!image) return 0;
      const imgAspect = image.width / image.height;

      // Vertical coverage not needed when vertical stacking tiles vertically
      const needsVertical = !config.allowVerticalStack;
      // Horizontal coverage not needed when seamless tiles horizontally
      const needsHorizontal = config.muralType !== 'seamless';

      const minForHeight = needsVertical
        ? (state.heightM * imgAspect) / state.baseWidthM
        : 0;
      const minForWidth = needsHorizontal
        ? state.widthM / state.baseWidthM
        : 0;

      return Math.max(minForHeight, minForWidth);
    },

    /**
     * Minimum zoom = max(config.zoomMin, coverageMin).
     * This is the effective lower bound shown on the slider.
     */
    getMinZoom(config, state, image) {
      if (!image) return config.zoomMin;
      const coverageMin = this.getCoverageMin(config, state, image);
      return Math.max(config.zoomMin, coverageMin);
    },

    /**
     * Effective max height so that coverageMin ≤ zoomMax.
     * heightM × imgAspect / baseWidthM ≤ zoomMax
     * → heightM ≤ baseWidthM × zoomMax / imgAspect
     * Note: this is CONSTANT (independent of widthM) for seamless mode.
     * For single mode, widthM/baseWidthM must also be ≤ zoomMax (handled by maxLanes).
     */
    getEffectiveMaxHeight(config, state, image) {
      if (!image || !config.allowZoom) return config.maxHeight;
      // Vertical stacking tiles vertically — no height-based zoom limit
      if (config.allowVerticalStack) return config.maxHeight;
      const imgAspect = image.width / image.height;
      const zoomLimited = (state.baseWidthM * config.zoomMax) / imgAspect;
      return Math.min(config.maxHeight, zoomLimited);
    },

    /**
     * Effective max lanes (single mode only).
     * widthM / baseWidthM ≤ zoomMax → widthM ≤ baseWidthM × zoomMax
     * Seamless mode: no width-based zoom constraint (tiles horizontally).
     */
    getEffectiveMaxLanes(config, state) {
      if (!config.allowZoom || config.muralType === 'seamless') {
        return Math.floor((config.maxWidth + EPS) / config.laneWidth);
      }
      // Single mode: zoom-based width limit
      const maxWidthFromZoom = state.baseWidthM * config.zoomMax;
      const maxWidth = Math.min(config.maxWidth, maxWidthFromZoom);
      return Math.floor((maxWidth + EPS) / config.laneWidth);
    },

    /**
     * Enforce zoom: clamp state.zoom to [minZoom, zoomMax].
     * For allowZoom=false, zoom is locked to coverageMin (auto-fill).
     * Returns the computed minZoom for UI updates.
     */
    enforceZoom(config, state, image) {
      if (!config.allowZoom) {
        // Auto-fill: zoom just enough to cover height (if not vertically tiled).
        // Width gaps are always filled by horizontal tiling when !allowZoom,
        // so we never zoom up to cover width — see _drawMuralImage.
        if (!image) { state.zoom = 1.0; return 1.0; }
        const imgAspect = image.width / image.height;
        const minForHeight = config.allowVerticalStack
          ? 0 : (state.heightM * imgAspect) / state.baseWidthM;
        state.zoom = Math.max(minForHeight, config.zoomMin);
        return state.zoom;
      }
      const minZoom = this.getMinZoom(config, state, image);
      state.zoom = Math.max(minZoom, Math.min(config.zoomMax, state.zoom));
      return minZoom;
    },

    /**
     * Clamp offsets so the mural image never leaves gaps in the wall.
     * Modifies state.offsetX and state.offsetY in place.
     */
    clampOffsets(config, state, renderer) {
      const dims = renderer.calcDimensions(state);
      if (!dims) return;

      const { wallPxW, wallPxH, muralPxW, muralPxH } = dims;

      // Vertical: no clamp when tiling vertically (explicit or auto-fill)
      if (config.allowVerticalStack || muralPxH < wallPxH - 1) {
        // Tiling vertically — no vertical clamp
      } else if (muralPxH >= wallPxH) {
        const maxY = (muralPxH - wallPxH) / 2;
        state.offsetY = clamp(state.offsetY, -maxY, maxY);
      } else {
        state.offsetY = 0;
      }

      // Horizontal: no clamp when tiling horizontally (seamless or auto-fill)
      if (config.muralType === 'seamless' || muralPxW < wallPxW - 1) {
        // Tiling horizontally — no horizontal clamp
      } else {
        if (muralPxW >= wallPxW) {
          const maxX = (muralPxW - wallPxW) / 2;
          state.offsetX = clamp(state.offsetX, -maxX, maxX);
        } else {
          state.offsetX = 0;
        }
      }

      // Apply offset step snapping (on release, not during drag)
      if (config.offsetStep) {
        // Offsets are in pixels; step is in % of wall dimension
        // This snap is applied in the pointer-up handler, not here
      }
    }
  };


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 7: CART MANAGER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * CartManager handles adding configured murals to the Shopify cart
   * via the Ajax Cart API.
   */
  const CartManager = {

    /**
     * Build the line item properties object from a configuration.
     * roundedArea is the area that matches the selected variant (rounded to 0.1 m²).
     */
    buildProperties(config, cartConfig, roundedArea) {
      const material = config.materials[cartConfig.materialIndex] || config.materials[0];
      const exactArea = cartConfig.widthM * cartConfig.heightM;
      // Use rounded area for pricing (matches the variant price)
      const pricingArea = roundedArea || (Math.round(exactArea * 10) / 10);
      const total = pricingArea * material.price_m2;

      const isNL = config.locale === 'nl';
      const props = {
        // Customer-visible properties (no underscore prefix)
        [isNL ? 'Muur breedte' : 'Wall Width']: `${Math.round(cartConfig.widthM * 100)} cm`,
        [isNL ? 'Muur hoogte' : 'Wall Height']: `${Math.round(cartConfig.heightM * 100)} cm`,
        [isNL ? 'Oppervlakte' : 'Area']: `${pricingArea.toFixed(1)} m²`,
        [isNL ? 'Prijs' : 'Configured Price']: formatPrice(total, config.locale),
      };

      if (config.materials.length > 1) {
        props[isNL ? 'Materiaal' : 'Material'] = material.name;
      }

      if (config.lanesType === 'strips') {
        props[isNL ? 'Aantal banen' : 'Number of Strips'] = String(cartConfig.lanes);
      }

      // Hidden properties (underscore prefix — hidden from customer)
      Object.assign(props, {
        '_wall_width_m': cartConfig.widthM.toFixed(4),
        '_wall_height_m': cartConfig.heightM.toFixed(4),
        '_area_m2': exactArea.toFixed(4),
        '_area_rounded': pricingArea.toFixed(1),
        '_num_lanes': String(cartConfig.lanes),
        '_lane_width_m': config.laneWidth.toFixed(4),
        '_zoom_level': cartConfig.zoom.toFixed(4),
        '_base_width_m': cartConfig.baseWidthM.toFixed(4),
        '_offset_x_px': cartConfig.offsetX.toFixed(2),
        '_offset_y_px': cartConfig.offsetY.toFixed(2),
        '_offset_x_pct': (cartConfig.offsetXPct != null ? cartConfig.offsetXPct : 50).toFixed(1),
        '_offset_y_pct': (cartConfig.offsetYPct != null ? cartConfig.offsetYPct : 50).toFixed(1),
        '_material': material.key,
        '_price_per_m2': material.price_m2.toFixed(2),
        '_total_price': total.toFixed(2),
        '_mural_type': config.muralType,
        '_config_version': VERSION,
      });

      return props;
    },

    /**
     * Find the variant ID for the closest size match.
     * sizeVariants is nested: { "MaterialName": { "3.0": id }, "_flat": { "3.0": id } }
     * materialName is used to select the right sub-map. Falls back to "_flat".
     * Returns the variant ID or null if no size variants exist.
     */
    findSizeVariant(sizeVariants, areaM2, materialName) {
      if (!sizeVariants) return null;

      // Find the right sub-map: try material name first (exact, then case-insensitive), then "_flat"
      let map = null;
      if (materialName) {
        map = sizeVariants[materialName];
        if (!map) {
          // Case-insensitive fallback
          const lower = materialName.toLowerCase();
          for (const key in sizeVariants) {
            if (key !== '_flat' && key.toLowerCase() === lower) {
              map = sizeVariants[key];
              break;
            }
          }
        }
      }
      if (!map) map = sizeVariants._flat || null;
      if (!map) return null;

      const keys = Object.keys(map);
      if (keys.length === 0) return null;

      // Round area to nearest 0.1 m²
      const rounded = Math.round(areaM2 * 10) / 10;
      const key = rounded.toFixed(1);

      // Exact match
      if (map[key]) return map[key];

      // Find closest match
      let closest = null;
      let closestDiff = Infinity;
      for (const k of keys) {
        const diff = Math.abs(parseFloat(k) - rounded);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = k;
        }
      }
      return closest ? map[closest] : null;
    },

    /**
     * Add a configured mural to the Shopify cart.
     * 
     * PRICING: Uses variant-based pricing. Each size (in 0.1 m² steps) has
     * its own variant with the correct pre-calculated price.
     * The JS finds the closest size variant and adds quantity = 1.
     * 
     * If no size variants are available (sizeVariants is empty), falls back
     * to using the material's variantId or the product's default variant.
     * 
     * Returns a Promise.
     */
    addToCart(config, cartConfig, sizeVariants) {
      const material = config.materials[cartConfig.materialIndex] || config.materials[0];
      const area = cartConfig.widthM * cartConfig.heightM;

      // Round area to nearest 0.1 m² — this determines both variant and displayed price
      const roundedArea = Math.round(area * 10) / 10;

      const properties = this.buildProperties(config, cartConfig, roundedArea);

      // Try to find a size variant for this area + material combination
      const sizeVariantId = this.findSizeVariant(sizeVariants || {}, area, material.name);
      const variantId = sizeVariantId || material.variantId || config.variantId;

      if (!sizeVariantId) {
        console.warn('[MuralConfigurator] No size variant found for area=' + roundedArea +
          ', material="' + (material.name || '?') + '". Falling back to variant ' + variantId);
      }

      const body = {
        items: [{
          id: variantId,
          quantity: 1,
          properties: properties,
        }]
      };

      return fetch(config.addToCartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(response => {
          if (!response.ok) {
            return response.json().catch(() => ({})).then(data => {
              const desc = data.description || data.message || response.status;
              throw new Error('Cart API error: ' + response.status + ' — ' + desc);
            });
          }
          return response.json();
        });
    },

  };


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 8: MANUFACTURER API CLIENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * ApiClient fetches configuration options from manufacturer APIs
   * via the app backend proxy.
   */
  const ApiClient = {

    /** Cache of API responses by provider+productId */
    _cache: {},

    /**
     * Fetch configuration options from the manufacturer API.
     * Returns a Promise that resolves with the API data, or null on failure.
     * Uses client-side caching (1 hour expiry).
     */
    fetchOptions(config, proxyBaseUrl) {
      if (!config.apiEnabled || !config.apiProvider || !config.apiProductId) {
        return Promise.resolve(null);
      }

      const cacheKey = `${config.apiProvider}:${config.apiProductId}`;
      const cached = this._cache[cacheKey];
      if (cached && Date.now() - cached.timestamp < 3600000) {
        return Promise.resolve(cached.data);
      }

      // Also check localStorage
      try {
        const stored = localStorage.getItem(`mural_api_${cacheKey}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Date.now() - parsed.timestamp < 3600000) {
            this._cache[cacheKey] = parsed;
            return Promise.resolve(parsed.data);
          }
        }
      } catch (e) { /* localStorage unavailable */ }

      const url = `${proxyBaseUrl}/api/manufacturer/${config.apiProvider}/${config.apiProductId}/options`;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000); // 3s timeout

      return fetch(url, { signal: controller.signal })
        .then(r => {
          clearTimeout(timeout);
          if (!r.ok) throw new Error(`API error: ${r.status}`);
          return r.json();
        })
        .then(data => {
          const entry = { data, timestamp: Date.now() };
          this._cache[cacheKey] = entry;
          try { localStorage.setItem(`mural_api_${cacheKey}`, JSON.stringify(entry)); }
          catch (e) { /* ignore */ }
          return data;
        })
        .catch(err => {
          clearTimeout(timeout);
          console.warn('[MuralConfigurator] API fetch failed, using metafield defaults:', err.message);
          return null;
        });
    },

    /**
     * Merge API response data into the config.
     * API values override metafield values where present.
     */
    mergeIntoConfig(config, apiData) {
      if (!apiData) return;

      if (apiData.zoomMin != null) config.zoomMin = apiData.zoomMin;
      if (apiData.zoomMax != null) config.zoomMax = apiData.zoomMax;
      if (apiData.maxWidth != null) config.maxWidth = apiData.maxWidth;
      if (apiData.maxHeight != null) config.maxHeight = apiData.maxHeight;
      if (apiData.minHeight != null) config.minHeight = apiData.minHeight;
      if (apiData.purchaseLink) config._apiPurchaseLink = apiData.purchaseLink;
      // Extend as needed based on actual API response shape
    }
  };


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 9: DRAG HANDLER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * DragHandler manages pointer-based dragging on the canvas.
   */
  class DragHandler {
    constructor(areaElement, onDrag, onDragEnd) {
      this.area = areaElement;
      this.onDrag = onDrag;
      this.onDragEnd = onDragEnd;
      this.active = false;
      this.startX = 0;
      this.startY = 0;
      this.startOffsetX = 0;
      this.startOffsetY = 0;
      this._bound = false;
    }

    /** Bind pointer events. Call once. */
    bind(getAllowPanX, getAllowPanY, getOffset) {
      if (this._bound) return;
      this._bound = true;

      this._getAllowPanX = getAllowPanX;
      this._getAllowPanY = getAllowPanY;
      this._getOffset = getOffset;

      this.area.addEventListener('pointerdown', this._onDown.bind(this));
      this.area.addEventListener('pointermove', this._onMove.bind(this));
      this.area.addEventListener('pointerup', this._onUp.bind(this));
      this.area.addEventListener('pointercancel', this._onUp.bind(this));
    }

    _onDown(e) {
      if (!this._getAllowPanX() && !this._getAllowPanY()) return;
      this.active = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      const offset = this._getOffset();
      this.startOffsetX = offset.x;
      this.startOffsetY = offset.y;
      this.area.setPointerCapture(e.pointerId);
      e.preventDefault();
    }

    _onMove(e) {
      if (!this.active) return;
      const dx = this._getAllowPanX() ? e.clientX - this.startX : 0;
      const dy = this._getAllowPanY() ? e.clientY - this.startY : 0;
      this.onDrag(this.startOffsetX + dx, this.startOffsetY + dy);
    }

    _onUp() {
      if (!this.active) return;
      this.active = false;
      if (this.onDragEnd) this.onDragEnd();
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 10: UI BUILDER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * UIBuilder creates all DOM elements for the configurator.
   * It returns references to key elements for the main controller to use.
   * 
   * This is separated from the controller so that theming/styling can be
   * customized without touching the logic.
   */
  class UIBuilder {
    constructor(config) {
      this.config = config;
      this.t = STRINGS[config.locale];
      this.refs = {};
    }

    /**
     * Build the product page elements (material selector, button, summaries).
     * Returns a document fragment to append to the container.
     */
    buildProductPage() {
      const frag = document.createDocumentFragment();
      const config = this.config;
      const t = this.t;

      // Material selector (only if multiple materials)
      if (config.materials.length > 1) {
        const matSection = el('div', { className: 'mc-material-selector' });
        const matLabel = el('label', { className: 'mc-label' }, t.materialLabel);
        matSection.appendChild(matLabel);

        const matOptions = el('div', { className: 'mc-material-options' });
        config.materials.forEach((mat, i) => {
          const btn = el('button', {
            className: `mc-material-btn${i === config._defaultMaterialIndex ? ' mc-active' : ''}`,
            'data-index': String(i),
          },
            el('span', { className: 'mc-mat-name' }, mat.name),
            el('span', { className: 'mc-mat-price' }, `${formatPrice(mat.price_m2, config.locale)}${t.pricePerM2}`)
          );
          matOptions.appendChild(btn);
        });
        matSection.appendChild(matOptions);
        frag.appendChild(matSection);
        this.refs.materialOptions = matOptions;
      }

      // Price per m² display
      const defaultMat = config.materials[config._defaultMaterialIndex] || config.materials[0];
      const priceDisplay = el('div', { className: 'mc-price-display' },
        el('span', { className: 'mc-price-value' }, defaultMat ? formatPrice(defaultMat.price_m2, config.locale) : ''),
        el('span', { className: 'mc-price-unit' }, ` ${t.pricePerM2}`)
      );
      frag.appendChild(priceDisplay);
      this.refs.priceDisplay = priceDisplay;
      this.refs.priceValue = priceDisplay.querySelector('.mc-price-value');

      // Configure button
      const configBtn = el('button', { className: 'mc-configure-btn' }, t.configureButton);
      frag.appendChild(configBtn);
      this.refs.configureBtn = configBtn;

      // Summaries container
      const summaries = el('div', { className: 'mc-summaries' });
      frag.appendChild(summaries);
      this.refs.summaries = summaries;

      // Cart link (hidden until first add)
      const cartLink = el('a', { className: 'mc-cart-link', href: config.cartUrl, style: { display: 'none' } }, t.goToCart);
      frag.appendChild(cartLink);
      this.refs.cartLink = cartLink;

      return frag;
    }

    /**
     * Build the full-screen modal.
     * Returns the modal element (not yet in the DOM).
     */
    buildModal() {
      const config = this.config;
      const t = this.t;

      // Overlay
      const overlay = el('div', { className: 'mc-modal-overlay' });
      this.refs.overlay = overlay;

      // Modal container
      const modal = el('div', { className: 'mc-modal' });

      // Header
      const header = el('div', { className: 'mc-modal-header' },
        el('span', { className: 'mc-modal-title' }, t.modalTitle(config.productName)),
        el('button', { className: 'mc-modal-close' }, '✕')
      );
      modal.appendChild(header);
      this.refs.modalClose = header.querySelector('.mc-modal-close');

      // Step 1: Wall size
      const step1 = el('div', { className: 'mc-step-wallsize' });
      const card = el('div', { className: 'mc-wallsize-card' });
      card.appendChild(el('h2', null, t.step1Title));
      card.appendChild(el('p', null, t.step1Subtitle));

      const dimInputs = el('div', { className: 'mc-dim-inputs' });

      // Width input
      const widthGroup = el('div', { className: 'mc-dim-group' });
      widthGroup.appendChild(el('label', null, t.widthLabel));
      const widthWrap = el('div', { className: 'mc-dim-wrap' });
      const widthInput = el('input', {
        type: 'number', className: 'mc-dim-input',
        min: String(Math.round(config.laneWidth * 100)),
        max: String(Math.round(config.maxWidth * 100)),
        step: '1',
        value: String(Math.round(config.defaultWidth * 100)),
      });
      widthWrap.appendChild(widthInput);
      widthWrap.appendChild(el('span', { className: 'mc-dim-unit' }, t.unitCm));
      widthGroup.appendChild(widthWrap);
      dimInputs.appendChild(widthGroup);
      this.refs.widthInput = widthInput;

      // Height input
      const heightGroup = el('div', { className: 'mc-dim-group' });
      heightGroup.appendChild(el('label', null, t.heightLabel));
      const heightWrap = el('div', { className: 'mc-dim-wrap' });
      const heightInput = el('input', {
        type: 'number', className: 'mc-dim-input',
        min: String(Math.round(config.minHeight * 100)),
        max: String(Math.round(config.maxHeight * 100)),
        step: String(Math.round(config.heightStep * 100) || '1'),
        value: String(Math.round(config.defaultHeight * 100)),
      });
      heightWrap.appendChild(heightInput);
      heightWrap.appendChild(el('span', { className: 'mc-dim-unit' }, t.unitCm));
      heightGroup.appendChild(heightWrap);
      dimInputs.appendChild(heightGroup);
      this.refs.heightInput = heightInput;

      card.appendChild(dimInputs);

      // Snapped info line
      const snappedInfo = el('div', { className: 'mc-snapped-info' });
      card.appendChild(snappedInfo);
      this.refs.snappedInfo = snappedInfo;

      // Continue button
      const continueBtn = el('button', { className: 'mc-continue-btn' }, t.continueBtn);
      card.appendChild(continueBtn);
      this.refs.continueBtn = continueBtn;

      step1.appendChild(card);
      modal.appendChild(step1);
      this.refs.step1 = step1;

      // Step 2: Configurator
      const step2 = el('div', { className: 'mc-step-configurator' });

      // Canvas area
      const canvasArea = el('div', { className: 'mc-canvas-area' });
      const canvas = el('canvas', { className: 'mc-canvas' });
      canvasArea.appendChild(canvas);
      step2.appendChild(canvasArea);
      this.refs.canvasArea = canvasArea;
      this.refs.canvas = canvas;

      // Control bar
      const controlBar = el('div', { className: 'mc-control-bar' });

      // Row 1: Controls
      const row1 = el('div', { className: 'mc-controls-row' });

      // Width control (+/- lanes)
      const widthCtrl = el('div', { className: 'mc-control-group' });
      widthCtrl.appendChild(el('span', { className: 'mc-ctrl-label' }, t.widthLabel));
      const widthCtrlValue = el('div', { className: 'mc-ctrl-value' });
      const laneMinusBtn = el('button', { className: 'mc-lane-btn' }, '−');
      const ctrlWidthDisplay = el('input', {
        type: 'text', className: 'mc-ctrl-input', readonly: 'readonly',
      });
      const lanePlusBtn = el('button', { className: 'mc-lane-btn' }, '+');
      widthCtrlValue.appendChild(laneMinusBtn);
      widthCtrlValue.appendChild(ctrlWidthDisplay);
      widthCtrlValue.appendChild(lanePlusBtn);
      widthCtrl.appendChild(widthCtrlValue);
      row1.appendChild(widthCtrl);
      this.refs.laneMinusBtn = laneMinusBtn;
      this.refs.lanePlusBtn = lanePlusBtn;
      this.refs.ctrlWidthDisplay = ctrlWidthDisplay;

      row1.appendChild(el('div', { className: 'mc-separator' }));

      // Height control
      const heightCtrl = el('div', { className: 'mc-control-group' });
      heightCtrl.appendChild(el('span', { className: 'mc-ctrl-label' }, t.heightLabel));
      const ctrlHeightInput = el('input', {
        type: 'number', className: 'mc-ctrl-input mc-ctrl-height',
        min: String(Math.round(config.minHeight * 100)),
        max: String(Math.round(config.maxHeight * 100)),
        step: String(Math.round(config.heightStep * 100) || '1'),
      });
      heightCtrl.appendChild(ctrlHeightInput);
      const ctrlHeightHint = el('span', { className: 'mc-ctrl-hint' });
      heightCtrl.appendChild(ctrlHeightHint);
      row1.appendChild(heightCtrl);
      this.refs.ctrlHeightInput = ctrlHeightInput;
      this.refs.ctrlHeightHint = ctrlHeightHint;

      // Zoom control (only if allowed)
      if (config.allowZoom) {
        row1.appendChild(el('div', { className: 'mc-separator' }));
        const zoomCtrl = el('div', { className: 'mc-control-group' });
        zoomCtrl.appendChild(el('span', { className: 'mc-ctrl-label' }, t.zoomLabel));
        const zoomSlider = el('input', {
          type: 'range', className: 'mc-zoom-slider',
          min: String(Math.round(config.zoomMin * 100)),
          max: String(Math.round(config.zoomMax * 100)),
          value: '100',
          step: config.zoomStep ? String(Math.round(config.zoomStep * 100)) : '1',
        });
        const zoomVal = el('span', { className: 'mc-zoom-val' }, '1.0×');
        const zoomHint = el('span', { className: 'mc-ctrl-hint' });
        zoomHint.style.display = 'none';
        zoomCtrl.appendChild(zoomSlider);
        zoomCtrl.appendChild(zoomVal);
        zoomCtrl.appendChild(zoomHint);
        row1.appendChild(zoomCtrl);
        this.refs.zoomSlider = zoomSlider;
        this.refs.zoomVal = zoomVal;
        this.refs.zoomHint = zoomHint;
      }

      controlBar.appendChild(row1);

      // Row 2: Price + Add to Cart
      const row2 = el('div', { className: 'mc-price-row' });
      const priceArea = el('div', { className: 'mc-price-area' });
      const priceTotal = el('span', { className: 'mc-price-total' });
      const priceInfoBtn = el('button', { className: 'mc-price-info-btn' }, 'i');
      const priceTooltip = el('div', { className: 'mc-price-tooltip' });
      priceInfoBtn.appendChild(priceTooltip);
      priceArea.appendChild(priceTotal);
      priceArea.appendChild(priceInfoBtn);
      row2.appendChild(priceArea);
      this.refs.priceTotal = priceTotal;
      this.refs.priceInfoBtn = priceInfoBtn;
      this.refs.priceTooltip = priceTooltip;

      const isApiPurchase = config.apiEnabled && config._apiPurchaseLink;
      const atcBtn = el('button', { className: 'mc-atc-btn' },
        isApiPurchase ? t.orderNow : t.addToCart
      );
      row2.appendChild(atcBtn);
      this.refs.atcBtn = atcBtn;

      // Area warning (hidden by default, replaces price row when area out of range)
      const areaWarn = el('div', { className: 'mc-area-warning' });
      const areaWarnMsg = el('span', { className: 'mc-area-msg' });
      const areaWarnBtn = el('button', { className: 'mc-atc-disabled' }, t.addToCart);
      areaWarn.appendChild(areaWarnMsg);
      areaWarn.appendChild(areaWarnBtn);
      this.refs.areaWarning = areaWarn;
      this.refs.areaMsg = areaWarnMsg;

      controlBar.appendChild(row2);
      controlBar.appendChild(areaWarn);

      step2.appendChild(controlBar);
      modal.appendChild(step2);
      this.refs.step2 = step2;
      this.refs.modal = modal;

      return { overlay, modal };
    }

    /**
     * Render a summary block for a cart configuration.
     * Returns a DOM element.
     */
    buildSummaryBlock(index, cartConfig) {
      const config = this.config;
      const t = this.t;
      const material = config.materials[cartConfig.materialIndex] || config.materials[0];
      const exactArea = cartConfig.widthM * cartConfig.heightM;
      const roundedArea = Math.round(exactArea * 10) / 10;
      const total = roundedArea * material.price_m2;

      const block = el('div', { className: 'mc-summary-block' });

      // Header with title and remove button
      const header = el('div', { className: 'mc-summary-header' },
        el('span', { className: 'mc-summary-title' }, t.wallLabel(index + 1)),
        el('button', {
          className: 'mc-summary-remove',
          title: t.removeTitle,
          'data-index': String(index),
        }, '✕')
      );
      block.appendChild(header);

      // Details line
      const details = el('div', { className: 'mc-summary-details' },
        `${Math.round(cartConfig.widthM * 100)} × ${Math.round(cartConfig.heightM * 100)} cm · ` +
        `${t.lanesLabel(cartConfig.lanes)} · ${roundedArea.toFixed(1)} m²`
      );
      block.appendChild(details);

      // Material dropdown + price + edit
      const row = el('div', { className: 'mc-summary-row' });

      if (config.materials.length > 1) {
        const select = el('select', {
          className: 'mc-summary-material-select',
          'data-index': String(index),
        });
        config.materials.forEach((mat, i) => {
          const opt = el('option', { value: String(i) },
            `${mat.name} — ${formatPrice(mat.price_m2, config.locale)}${t.pricePerM2}`
          );
          if (i === cartConfig.materialIndex) opt.selected = true;
          select.appendChild(opt);
        });
        row.appendChild(select);
      }

      const priceSpan = el('span', { className: 'mc-summary-price', 'data-index': String(index) },
        formatPrice(total, config.locale)
      );
      row.appendChild(priceSpan);

      const editLink = el('a', {
        className: 'mc-summary-edit',
        'data-index': String(index),
      }, t.editLink);
      row.appendChild(editLink);

      block.appendChild(row);

      return block;
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 11: MAIN CONTROLLER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * MuralConfiguratorController is the main orchestrator.
   * It ties together config, state, UI, renderer, and cart.
   */
  class MuralConfiguratorController {
    constructor(container) {
      this.container = container;
      this.config = parseConfig(container);
      if (!this.config) return;

      // Parse size → variant ID map for area-based variant pricing.
      // Two sources:
      //   1. data-size-variants-json: JSON object from metafield (preferred, no 250-variant limit)
      //      Format: { "Vlies": { "3.0": 12345, "3.1": 12346 }, "Vinyl Sand": { "3.0": 12347 } }
      //   2. data-size-variants: comma-separated string (fallback, built from Liquid loop)
      //      Format: "Vlies|3.0:12345,Vlies|3.1:12346,Vinyl Sand|3.0:12347"
      this.sizeVariants = {};
      try {
        const svJson = container.getAttribute('data-size-variants-json');
        if (svJson) {
          // JSON metafield: already in the right nested format, but values might be strings
          const parsed = JSON.parse(svJson);
          for (const mat in parsed) {
            if (!parsed.hasOwnProperty(mat)) continue;
            this.sizeVariants[mat] = {};
            for (const area in parsed[mat]) {
              if (!parsed[mat].hasOwnProperty(area)) continue;
              this.sizeVariants[mat][area] = parseInt(parsed[mat][area]);
            }
          }
        } else {
          const svRaw = container.getAttribute('data-size-variants');
          if (svRaw) {
            svRaw.split(',').forEach(pair => {
              const colonIdx = pair.indexOf(':');
              if (colonIdx < 0) return;
              const left = pair.substring(0, colonIdx).trim();
              const id = parseInt(pair.substring(colonIdx + 1).trim());
              if (!id) return;
              const pipeIdx = left.indexOf('|');
              if (pipeIdx >= 0) {
                const mat = left.substring(0, pipeIdx).trim();
                const area = left.substring(pipeIdx + 1).trim();
                if (!this.sizeVariants[mat]) this.sizeVariants[mat] = {};
                this.sizeVariants[mat][area] = id;
              } else {
                if (!this.sizeVariants._flat) this.sizeVariants._flat = {};
                this.sizeVariants._flat[left] = id;
              }
            });
          }
        }
      } catch(e) {
        console.warn('[MuralConfigurator] Could not parse size variants:', e);
      }

      this.state = createState(this.config);
      this.savedState = null;
      this.cartConfigs = [];       // array of saved configurations
      this.editingIndex = -1;      // -1 = new, >= 0 = editing

      this.ui = new UIBuilder(this.config);
      this.renderer = null;
      this.dragHandler = null;

      this._init();
    }

    // ── Initialization ──

    _init() {
      // Build product page UI
      const pageFrag = this.ui.buildProductPage();
      this.container.appendChild(pageFrag);

      // Build modal
      const { overlay, modal } = this.ui.buildModal();
      document.body.appendChild(overlay);
      document.body.appendChild(modal);

      // Create renderer (not active until modal step 2)
      this.renderer = new CanvasRenderer(this.ui.refs.canvas, this.ui.refs.canvasArea);

      // Bind events
      this._bindProductPageEvents();
      this._bindModalEvents();

      // If API-enabled, fetch options
      if (this.config.apiEnabled) {
        this._fetchApiOptions();
      }

      // Handle initial validation
      this._updateValidation();
    }

    // ── Product Page Events ──

    _bindProductPageEvents() {
      const refs = this.ui.refs;

      // Configure button
      refs.configureBtn.addEventListener('click', () => this.openModal());

      // Material selector
      if (refs.materialOptions) {
        refs.materialOptions.addEventListener('click', (e) => {
          const btn = e.target.closest('.mc-material-btn');
          if (!btn) return;
          refs.materialOptions.querySelectorAll('.mc-material-btn').forEach(b => b.classList.remove('mc-active'));
          btn.classList.add('mc-active');
          const index = parseInt(btn.dataset.index);
          this.state.materialIndex = index;
          const mat = this.config.materials[index];
          if (mat && refs.priceValue) {
            refs.priceValue.textContent = formatPrice(mat.price_m2, this.config.locale);
          }
          // Update total price in step 2 if it's visible
          if (this.state.step === 2) {
            this._updatePrice();
          }
        });
      }

      // Summaries container (delegated events)
      refs.summaries.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.mc-summary-remove');
        if (removeBtn) {
          this._removeConfig(parseInt(removeBtn.dataset.index));
          return;
        }
        const editLink = e.target.closest('.mc-summary-edit');
        if (editLink) {
          this._editConfig(parseInt(editLink.dataset.index));
          return;
        }
      });

      refs.summaries.addEventListener('change', (e) => {
        const select = e.target.closest('.mc-summary-material-select');
        if (select) {
          this._changeConfigMaterial(parseInt(select.dataset.index), parseInt(select.value));
        }
      });
    }

    // ── Modal Events ──

    _bindModalEvents() {
      const refs = this.ui.refs;

      // Close button
      refs.modalClose.addEventListener('click', () => this.closeModal());

      // Overlay click to close
      refs.overlay.addEventListener('click', () => this.closeModal());

      // Step 1 inputs
      refs.widthInput.addEventListener('input', () => this._updateValidation());
      refs.heightInput.addEventListener('input', () => this._updateValidation());
      refs.continueBtn.addEventListener('click', () => this._goToStep2());

      // Step 2 controls
      refs.laneMinusBtn.addEventListener('click', () => this._changeLanes(-1));
      refs.lanePlusBtn.addEventListener('click', () => this._changeLanes(1));
      refs.ctrlHeightInput.addEventListener('input', () => this._onCtrlHeightChange());
      refs.ctrlHeightInput.addEventListener('change', () => this._onCtrlHeightCommit());
      refs.ctrlHeightInput.addEventListener('blur', () => this._onCtrlHeightCommit());

      if (refs.zoomSlider) {
        refs.zoomSlider.addEventListener('input', () => this._onZoomChange());
      }

      // Price info tooltip
      refs.priceInfoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        refs.priceTooltip.classList.toggle('mc-visible');
      });
      document.addEventListener('click', () => {
        refs.priceTooltip.classList.remove('mc-visible');
      });

      // Add to Cart
      refs.atcBtn.addEventListener('click', () => this._addToCart());

      // Keyboard: Escape to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._isModalOpen()) this.closeModal();
      });

      // Window resize
      window.addEventListener('resize', () => {
        if (this.state.step === 2 && this._isModalOpen()) {
          this._enforceAndRender();
        }
      });
    }

    // ── Modal Open / Close ──

    openModal() {
      const refs = this.ui.refs;
      refs.overlay.classList.add('mc-open');
      refs.modal.classList.add('mc-open');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        refs.overlay.classList.add('mc-visible');
        refs.modal.classList.add('mc-visible');
      });

      // Restore saved state if exists, but keep current materialIndex
      // (user may have changed material on the product page since closing)
      if (this.savedState) {
        const currentMaterialIndex = this.state.materialIndex;
        this.state = cloneState(this.savedState);
        this.state.materialIndex = currentMaterialIndex;
        if (this.state.step === 2) {
          this._showStep2();
        }
      }
    }

    closeModal(addedToCart = false) {
      const refs = this.ui.refs;
      refs.overlay.classList.remove('mc-visible');
      refs.modal.classList.remove('mc-visible');
      document.body.style.overflow = '';

      if (addedToCart) {
        this.savedState = null;
        this._showToast(this.ui.t.addedToast);
      } else if (this.state.step > 1) {
        this.savedState = cloneState(this.state);
        this._showToast(this.ui.t.savedToast);
      }

      this.editingIndex = -1;

      setTimeout(() => {
        refs.overlay.classList.remove('mc-open');
        refs.modal.classList.remove('mc-open');
      }, 350);
    }

    _isModalOpen() {
      return this.ui.refs.modal.classList.contains('mc-open');
    }

    _showToast(message) {
      // Check if toast element exists, create if not
      let toast = document.querySelector('.mc-toast');
      if (!toast) {
        toast = el('div', { className: 'mc-toast' });
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('mc-visible');
      setTimeout(() => toast.classList.remove('mc-visible'), 2500);
    }

    // ── Step Navigation ──

    _goToStep2() {
      // Parse and apply step 1 inputs
      const widthCm = parseInt(this.ui.refs.widthInput.value) || Math.round(this.config.defaultWidth * 100);
      const heightCm = parseInt(this.ui.refs.heightInput.value) || Math.round(this.config.defaultHeight * 100);

      const snap = snapWidthUp(widthCm, this.config.laneWidth, this.config.maxWidth);
      this.state.widthM = snap.widthM;
      this.state.lanes = snap.lanes;

      const clampedHeight = clamp(heightCm / 100, this.config.minHeight, this.config.maxHeight);
      this.state.heightM = snapToStep(clampedHeight, this.config.heightStep, this.config.minHeight);

      this.state.step = 2;
      if (this.editingIndex < 0) {
        this.state.offsetX = 0;
        this.state.offsetY = 0;
      }

      this._showStep2();
    }

    _showStep2() {
      const refs = this.ui.refs;

      refs.step1.style.display = 'none';
      refs.step2.style.display = 'flex';

      // Update controls
      refs.ctrlWidthDisplay.value = `${Math.round(this.state.widthM * 100)} cm`;
      refs.ctrlHeightInput.value = Math.round(this.state.heightM * 100);

      if (refs.zoomSlider) {
        refs.zoomSlider.value = Math.round(this.state.zoom * 100);
        refs.zoomVal.textContent = this.state.zoom.toFixed(2) + '×';
      }

      this._updatePrice();

      // Load image and render
      const imageUrl = this.config.previewImage || this.config.productImage;
      this.renderer.loadImage(imageUrl).then(() => {
        // After the image loads, check if current dimensions are compatible
        // with zoom constraints. If height is too tall for the current width
        // and zoom range, reduce it to the effective maximum.
        if (this.config.allowZoom && this.renderer.image) {
          const effectiveMaxH = BoundaryEnforcer.getEffectiveMaxHeight(
            this.config, this.state, this.renderer.image
          );
          if (this.state.heightM > effectiveMaxH) {
            this.state.heightM = snapToStep(
              Math.min(this.state.heightM, effectiveMaxH),
              this.config.heightStep,
              this.config.minHeight
            );
            refs.ctrlHeightInput.value = Math.round(this.state.heightM * 100);
          }
        }
        this._enforceAndRender();
        this._initDrag();
      });
    }

    _showStep1() {
      const refs = this.ui.refs;
      refs.step1.style.display = '';
      refs.step2.style.display = '';
      refs.step1.style.display = 'flex';
      refs.step2.style.display = 'none';
    }

    _resetToStep1() {
      this.state = createState(this.config);
      this.savedState = null;
      this.ui.refs.widthInput.value = Math.round(this.config.defaultWidth * 100);
      this.ui.refs.heightInput.value = Math.round(this.config.defaultHeight * 100);
      this._updateValidation();
      this._showStep1();
    }

    // ── Step 1 Helpers ──

    _updateSnappedInfo() {
      // Called internally — just update the snapped width text
      const widthCm = parseInt(this.ui.refs.widthInput.value) || 0;
      const snap = snapWidthUp(widthCm, this.config.laneWidth, this.config.maxWidth);
      const laneCm = Math.round(this.config.laneWidth * 100);
      this.ui.refs.snappedInfo.textContent = this.ui.t.snappedInfo(snap.widthCm, snap.lanes, laneCm);
    }

    _updateValidation() {
      const t = this.ui.t;
      const refs = this.ui.refs;
      const config = this.config;
      const widthCm = parseInt(refs.widthInput.value) || 0;
      const heightCm = parseInt(refs.heightInput.value) || 0;
      const minWidthCm = Math.round(config.minWidth * 100);
      const maxWidthCm = Math.round(config.maxWidth * 100);
      const minHeightCm = Math.round(config.minHeight * 100);
      const maxHeightCm = Math.round(config.maxHeight * 100);

      let messages = [];
      let valid = true;

      // Width validation
      if (widthCm > 0 && widthCm < minWidthCm) {
        messages.push(t.widthTooSmall(minWidthCm));
        valid = false;
        refs.widthInput.style.borderColor = '#c0392b';
      } else if (widthCm > maxWidthCm) {
        messages.push(t.widthWarning(maxWidthCm));
        valid = false;
        refs.widthInput.style.borderColor = '#c0392b';
      } else {
        refs.widthInput.style.borderColor = '';
        if (widthCm > 0) {
          const snap = snapWidthUp(widthCm, config.laneWidth, config.maxWidth);
          const laneCm = Math.round(config.laneWidth * 100);
          messages.push(t.snappedInfo(snap.widthCm, snap.lanes, laneCm));
        }
      }

      // Height validation
      if (heightCm > 0 && (heightCm < minHeightCm || heightCm > maxHeightCm)) {
        messages.push(t.heightWarning(minHeightCm, maxHeightCm));
        valid = false;
        refs.heightInput.style.borderColor = '#c0392b';
      } else {
        refs.heightInput.style.borderColor = '';
      }

      // Area validation against variant range
      if (valid && widthCm > 0 && heightCm > 0) {
        const snapped = snapWidthUp(widthCm, config.laneWidth, config.maxWidth);
        // Normalize height the same way step 2 does (clamp + snapToStep)
        const normalizedHeightM = snapToStep(
          clamp(heightCm / 100, config.minHeight, config.maxHeight),
          config.heightStep,
          config.minHeight
        );
        const areaM2 = Math.round(snapped.widthM * normalizedHeightM * 10) / 10;

        // Determine effective limits from size variants (nested: { material: { area: id } })
        let effectiveMin = config.minArea || 0;
        let effectiveMax = config.maxArea || Infinity;
        if (this.sizeVariants) {
          // Collect all area keys across all materials
          const allAreas = [];
          for (const matKey of Object.keys(this.sizeVariants)) {
            const sub = this.sizeVariants[matKey];
            if (sub && typeof sub === 'object') {
              for (const a of Object.keys(sub)) allAreas.push(parseFloat(a));
            }
          }
          if (allAreas.length > 0) {
            const variantMin = Math.min(...allAreas);
            const variantMax = Math.max(...allAreas);
            if (variantMin > effectiveMin) effectiveMin = variantMin;
            if (variantMax < effectiveMax) effectiveMax = variantMax;
          }
        }

        const locale = config.locale;
        const decSep = locale === 'nl' ? ',' : '.';
        const fmtArea = (v) => v.toFixed(1).replace('.', decSep);

        if (effectiveMin > 0 && areaM2 < effectiveMin) {
          messages.push(t.minAreaWarning(fmtArea(effectiveMin), fmtArea(areaM2)));
          valid = false;
        } else if (effectiveMax < Infinity && areaM2 > effectiveMax) {
          messages.push(t.maxAreaWarning(fmtArea(effectiveMax), fmtArea(areaM2)));
          valid = false;
        } else {
          messages.push(t.areaInfo(fmtArea(areaM2)));
        }
      }

      // Update info line
      refs.snappedInfo.textContent = messages.join(' · ');
      refs.snappedInfo.style.color = valid ? '' : '#c0392b';

      // Enable/disable continue button
      const hasValues = widthCm > 0 && heightCm > 0;
      refs.continueBtn.disabled = !valid || !hasValues;
    }

    // ── Step 2 Controls ──

    _changeLanes(delta) {
      const config = this.config;
      const maxLanes = BoundaryEnforcer.getEffectiveMaxLanes(config, this.state);
      const minLanes = Math.ceil((config.minWidth - EPS) / config.laneWidth);
      const newLanes = clamp(this.state.lanes + delta, minLanes, maxLanes);
      if (newLanes === this.state.lanes) return;

      this.state.lanes = newLanes;
      this.state.widthM = newLanes * config.laneWidth;
      this.ui.refs.ctrlWidthDisplay.value = `${Math.round(this.state.widthM * 100)} cm`;
      // Absolute zoom: no compensation needed. baseWidthM is fixed,
      // zoom stays the same. Only minZoom is recalculated by _enforceAndRender.
      this._enforceAndRender();
      this._updatePrice();
    }

    _onCtrlHeightChange() {
      const input = this.ui.refs.ctrlHeightInput;
      const hint = this.ui.refs.ctrlHeightHint;
      const t = this.ui.t;
      const rawVal = parseInt(input.value);

      // Don't process empty or invalid input (user is still typing)
      if (isNaN(rawVal) || rawVal <= 0) return;

      const minCm = Math.round(this.config.minHeight * 100);
      // Compute effective max height (respects zoom constraints when applicable)
      let maxCm = Math.round(this.config.maxHeight * 100);
      if (this.config.allowZoom && this.renderer.image) {
        const effectiveMaxH = BoundaryEnforcer.getEffectiveMaxHeight(
          this.config, this.state, this.renderer.image
        );
        maxCm = Math.min(maxCm, Math.floor(effectiveMaxH * 100));
      }

      const clamped = clamp(rawVal / 100, this.config.minHeight, maxCm / 100);
      this.state.heightM = snapToStep(clamped, this.config.heightStep, this.config.minHeight);

      // Show warning if value is out of range, but DON'T modify input.value
      // (the user may still be typing — value is corrected on blur/change)
      if (rawVal < minCm || rawVal > maxCm) {
        input.style.borderColor = '#c0392b';
        hint.textContent = t.heightWarning(minCm, maxCm);
        hint.style.display = '';
        clearTimeout(this._heightHintTimer);
        this._heightHintTimer = setTimeout(() => {
          input.style.borderColor = '';
          hint.style.display = 'none';
        }, 3000);
      } else {
        input.style.borderColor = '';
        hint.style.display = 'none';
      }

      this._enforceAndRender();
      this._updatePrice();
    }

    /**
     * Commit the height value on blur/change — corrects the input
     * to the actual clamped state value.
     */
    _onCtrlHeightCommit() {
      const input = this.ui.refs.ctrlHeightInput;
      input.value = Math.round(this.state.heightM * 100);
      input.style.borderColor = '';
      this.ui.refs.ctrlHeightHint.style.display = 'none';
    }

    _onZoomChange() {
      const val = parseInt(this.ui.refs.zoomSlider.value);
      this.state.zoom = val / 100;

      // Enforce both minimum (coverage) and maximum (configured) bounds
      BoundaryEnforcer.enforceZoom(this.config, this.state, this.renderer.image);
      const minZoom = BoundaryEnforcer.getMinZoom(this.config, this.state, this.renderer.image);

      // Update slider range: ensure min never exceeds max
      const sliderMax = Math.round(this.config.zoomMax * 100);
      const sliderMin = Math.min(Math.ceil(minZoom * 100), sliderMax);
      this.ui.refs.zoomSlider.min = sliderMin;
      this.ui.refs.zoomSlider.value = Math.round(this.state.zoom * 100);

      this.ui.refs.zoomVal.textContent = this.state.zoom.toFixed(2) + '×';
      BoundaryEnforcer.clampOffsets(this.config, this.state, this.renderer);
      this.renderer.render(this.state, this.config);
    }

    _enforceAndRender() {
      const minZoom = BoundaryEnforcer.enforceZoom(this.config, this.state, this.renderer.image);
      if (this.ui.refs.zoomSlider) {
        // Ensure slider min never exceeds slider max
        const sliderMax = Math.round(this.config.zoomMax * 100);
        const sliderMin = Math.min(Math.ceil(minZoom * 100), sliderMax);
        this.ui.refs.zoomSlider.min = sliderMin;
        this.ui.refs.zoomSlider.value = Math.round(this.state.zoom * 100);
        this.ui.refs.zoomVal.textContent = this.state.zoom.toFixed(2) + '×';

        // Show warning when dimensions are impossible for the allowed zoom range
        if (this.ui.refs.zoomHint) {
          if (minZoom > this.config.zoomMax) {
            this.ui.refs.zoomHint.textContent = this.ui.t.zoomSizeWarning;
            this.ui.refs.zoomHint.style.display = '';
            this.ui.refs.zoomHint.style.color = '#c0392b';
          } else {
            this.ui.refs.zoomHint.textContent = '';
            this.ui.refs.zoomHint.style.display = 'none';
          }
        }
      }
      // Update height input max attribute to reflect zoom-based constraints
      if (this.config.allowZoom && this.renderer.image && this.ui.refs.ctrlHeightInput) {
        const effectiveMaxH = BoundaryEnforcer.getEffectiveMaxHeight(
          this.config, this.state, this.renderer.image
        );
        this.ui.refs.ctrlHeightInput.max = Math.floor(effectiveMaxH * 100);
      }
      BoundaryEnforcer.clampOffsets(this.config, this.state, this.renderer);
      this.renderer.render(this.state, this.config);
    }

    _initDrag() {
      if (this.dragHandler) return;
      this.dragHandler = new DragHandler(
        this.ui.refs.canvasArea,
        (newOffsetX, newOffsetY) => {
          this.state.offsetX = newOffsetX;
          this.state.offsetY = newOffsetY;
          BoundaryEnforcer.clampOffsets(this.config, this.state, this.renderer);
          this.renderer.render(this.state, this.config);
        },
        () => {
          // On drag end: snap offsets if step is defined
          if (this.config.offsetStep) {
            // Convert step percentage to pixels (approximate)
            // Full implementation would calculate based on wall pixel size
          }
        }
      );
      this.dragHandler.bind(
        () => this.config.allowPanX,
        () => this.config.allowPanY,
        () => ({ x: this.state.offsetX, y: this.state.offsetY })
      );
    }

    // ── Pricing ──

    _updatePrice() {
      const mat = this.config.materials[this.state.materialIndex] || this.config.materials[0];
      if (!mat) return;

      const exactArea = this.state.widthM * this.state.heightM;
      // Round to nearest 0.1 m² to match the variant that will be charged
      const roundedArea = Math.round(exactArea * 10) / 10;
      const total = roundedArea * mat.price_m2;
      const t = this.ui.t;
      const locale = this.config.locale;

      this.ui.refs.priceTotal.textContent = formatPrice(total, locale);

      // Tooltip content
      this.ui.refs.priceTooltip.textContent = t.priceInfoBreakdown(
        Math.round(this.state.widthM * 100),
        Math.round(this.state.heightM * 100),
        roundedArea.toFixed(1),
        mat.name,
        formatPrice(mat.price_m2, locale)
      );

      // Min area check — first check variant range, then merchant config
      const minArea = this.config.minArea;
      const maxArea = this.config.maxArea;

      // Determine effective limits from size variants (nested: { material: { area: id } })
      let variantMin = null;
      let variantMax = null;
      if (this.sizeVariants) {
        const allAreas = [];
        for (const matKey of Object.keys(this.sizeVariants)) {
          const sub = this.sizeVariants[matKey];
          if (sub && typeof sub === 'object') {
            for (const a of Object.keys(sub)) allAreas.push(parseFloat(a));
          }
        }
        if (allAreas.length > 0) {
          variantMin = Math.min(...allAreas);
          variantMax = Math.max(...allAreas);
        }
      }

      // Use the most restrictive limit
      const effectiveMin = Math.max(minArea || 0, variantMin || 0) || null;
      const effectiveMax = maxArea
        ? (variantMax ? Math.min(maxArea, variantMax) : maxArea)
        : variantMax;

      const decSep = locale === 'nl' ? ',' : '.';
      const fmtArea = (v) => v.toFixed(1).replace('.', decSep);

      let areaError = null;
      if (effectiveMin && roundedArea < effectiveMin) {
        areaError = t.minAreaWarning(fmtArea(effectiveMin), fmtArea(roundedArea));
      } else if (effectiveMax && roundedArea > effectiveMax) {
        areaError = t.maxAreaWarning(fmtArea(effectiveMax), fmtArea(roundedArea));
      }

      if (areaError) {
        this.ui.refs.priceTotal.parentElement.parentElement.style.display = 'none'; // hide price row
        this.ui.refs.areaWarning.style.display = 'flex';
        this.ui.refs.areaMsg.textContent = areaError;
      } else {
        this.ui.refs.priceTotal.parentElement.parentElement.style.display = '';
        this.ui.refs.areaWarning.style.display = 'none';
      }
    }

    // ── Cart Actions ──

    _addToCart() {
      // Block if area is outside valid range
      if (this.ui.refs.areaWarning.style.display !== 'none') {
        return;
      }

      const cartConfig = {
        widthM: this.state.widthM,
        heightM: this.state.heightM,
        lanes: this.state.lanes,
        zoom: this.state.zoom,
        baseWidthM: this.state.baseWidthM,
        offsetX: this.state.offsetX,
        offsetY: this.state.offsetY,
        materialIndex: this.state.materialIndex,
      };

      // Compute offset percentages (0% = one edge, 50% = centered, 100% = other edge)
      const dims = this.renderer.calcDimensions(this.state);
      if (dims) {
        const overflowX = Math.max(0, dims.muralPxW - dims.wallPxW);
        const overflowY = Math.max(0, dims.muralPxH - dims.wallPxH);
        cartConfig.offsetXPct = overflowX > EPS
          ? ((this.state.offsetX + overflowX / 2) / overflowX) * 100 : 50;
        cartConfig.offsetYPct = overflowY > EPS
          ? ((this.state.offsetY + overflowY / 2) / overflowY) * 100 : 50;
      } else {
        cartConfig.offsetXPct = 50;
        cartConfig.offsetYPct = 50;
      }

      // Check for API purchase link redirect
      if (this.config._apiPurchaseLink) {
        window.open(this.config._apiPurchaseLink, '_blank');
        this.closeModal(true);
        return;
      }

      // Add to Shopify cart
      CartManager.addToCart(this.config, cartConfig, this.sizeVariants)
        .then(() => {
          if (this.editingIndex >= 0) {
            this.cartConfigs[this.editingIndex] = cartConfig;
            this.editingIndex = -1;
          } else {
            this.cartConfigs.push(cartConfig);
          }

          this._renderSummaries();
          this.ui.refs.configureBtn.textContent = this.ui.t.configureAnother;
          this.ui.refs.cartLink.style.display = '';

          this._resetToStep1();
          this.closeModal(true);

          // Update the theme's cart count bubble
          this._updateCartCount();

          // Reset the theme's price display back to per-m² price
          // (adding a size variant can cause the theme to show the variant total)
          // Delay slightly to run after the theme's own variant-change handlers
          setTimeout(() => this._resetThemePrice(), 300);
          setTimeout(() => this._resetThemePrice(), 1000);
        })
        .catch(err => {
          console.error('[MuralConfigurator] Add to cart failed:', err);
          this._showToast(this.config.locale === 'nl'
            ? 'Er ging iets mis. Probeer het opnieuw.'
            : 'Something went wrong. Please try again.');
        });
    }

    // ── Summary Management ──

    _updateCartCount() {
      // 1. Fetch updated cart data
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          const count = cart.item_count;

          // 2. Update common cart count selectors
          const selectors = [
            '.cart-count',
            '.cart-count-bubble span',
            '[data-cart-count]',
            '.cart-item-count',
            '.js-cart-count',
            '#cart-icon-bubble span[aria-hidden]',
            '.header__cart-count',
          ];
          selectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(el => {
              el.textContent = String(count);
            });
          });
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.setAttribute('data-cart-count', String(count));
          });

          // 3. Show cart count bubbles that might be hidden when empty
          document.querySelectorAll('.cart-count-bubble, [data-cart-count-bubble]').forEach(el => {
            if (count > 0) el.removeAttribute('hidden');
          });

          // 4. Fire custom events that themes listen for
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          // 5. Try Shopify Section Rendering API to refresh cart sections
          // This refreshes the cart drawer/icon in themes that use sections
          this._refreshCartSections();
        })
        .catch(() => {});
    }

    _refreshCartSections() {
      // Refresh only cart-related sections (header, cart drawer, etc.)
      // Explicitly exclude product sections to avoid overwriting the product page
      const sectionsToRefresh = [];

      document.querySelectorAll('[id^="shopify-section-"]').forEach(section => {
        const id = section.id.replace('shopify-section-', '');
        // Only refresh header and cart sections, NOT product sections
        const isCartRelated = id.includes('cart') || id.includes('header') || id.includes('announcement');
        const isProduct = id.includes('product') || id.includes('main');
        if (isCartRelated && !isProduct) {
          sectionsToRefresh.push(id);
        }
      });

      if (sectionsToRefresh.length === 0) return;

      const url = '/cart?sections=' + sectionsToRefresh.join(',');
      fetch(url)
        .then(r => r.json())
        .then(sections => {
          for (const [id, html] of Object.entries(sections)) {
            const target = document.getElementById('shopify-section-' + id);
            if (target) {
              target.innerHTML = new DOMParser()
                .parseFromString(html, 'text/html')
                .querySelector('.shopify-section')?.innerHTML || html;
            }
          }
        })
        .catch(() => {});
    }

    /**
     * Reset the theme's product price display back to the per-m² price.
     * When we add a size variant to cart, the theme may detect the variant
     * change and update its price display to the variant total.
     * This restores it to the base price per m².
     */
    _resetThemePrice() {
      const mat = this.config.materials[this.state.materialIndex] || this.config.materials[0];
      if (!mat) return;

      const priceM2 = mat.price_m2;
      const locale = this.config.locale;

      // Reset our own price display
      if (this.ui.refs.priceValue) {
        this.ui.refs.priceValue.textContent = formatPrice(priceM2, locale);
      }

      // Also update the unit label if it exists
      const unitSpan = this.ui.refs.priceDisplay?.querySelector('.mc-price-unit');
      if (unitSpan) {
        unitSpan.textContent = '/m²';
      }

      // Reset common theme price selectors
      // Shopify themes typically display variant prices in these elements
      const priceSelectors = [
        '.price__regular .price-item--regular',
        '.price__sale .price-item--sale',
        '.product__price',
        '.product-price',
        '[data-product-price]',
        '.price-item--regular',
        '.current-price',
      ];

      const formattedM2 = formatPrice(priceM2, locale);
      
      // Convert to Shopify money format (theme might use different formatting)
      // We set both our format and try to set Shopify's format
      priceSelectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          // Only reset if the element is inside the product section (not cart)
          if (el.closest('[id*="cart"]') || el.closest('.mc-summary-block')) return;
          el.textContent = formattedM2;
        });
      });
    }

    _renderSummaries() {
      const container = this.ui.refs.summaries;
      container.innerHTML = '';

      this.cartConfigs.forEach((cfg, i) => {
        const block = this.ui.buildSummaryBlock(i, cfg);
        container.appendChild(block);
      });
    }

    _removeConfig(index) {
      this.cartConfigs.splice(index, 1);
      this._renderSummaries();

      if (this.cartConfigs.length === 0) {
        this.ui.refs.configureBtn.textContent = this.ui.t.configureButton;
        this.ui.refs.cartLink.style.display = 'none';
      }

      // TODO: Also remove from Shopify cart via Cart API
    }

    _editConfig(index) {
      const cfg = this.cartConfigs[index];
      this.editingIndex = index;

      this.state = {
        widthM: cfg.widthM,
        heightM: cfg.heightM,
        lanes: cfg.lanes,
        zoom: cfg.zoom,
        baseWidthM: cfg.baseWidthM || this.config.baseWidth || this.config.defaultWidth,
        offsetX: cfg.offsetX,
        offsetY: cfg.offsetY,
        materialIndex: cfg.materialIndex,
        step: 2,
      };

      this.savedState = null;
      this.openModal();
    }

    _changeConfigMaterial(index, materialIndex) {
      if (!this.cartConfigs[index]) return;
      this.cartConfigs[index].materialIndex = materialIndex;

      const mat = this.config.materials[materialIndex];
      const cfg = this.cartConfigs[index];
      const exactArea = cfg.widthM * cfg.heightM;
      const roundedArea = Math.round(exactArea * 10) / 10;
      const total = roundedArea * mat.price_m2;

      const priceSpan = this.ui.refs.summaries.querySelector(`.mc-summary-price[data-index="${index}"]`);
      if (priceSpan) {
        priceSpan.textContent = formatPrice(total, this.config.locale);
      }

      // TODO: Update the cart line item via Cart API
    }

    // ── API Integration ──

    _fetchApiOptions() {
      // The proxy base URL would come from the app's configuration
      // For now, this is a placeholder
      const proxyBaseUrl = this.container.dataset.apiProxy || '';
      if (!proxyBaseUrl) return;

      ApiClient.fetchOptions(this.config, proxyBaseUrl).then(data => {
        if (data) {
          ApiClient.mergeIntoConfig(this.config, data);
          // Re-render any affected UI
        } else if (this.config.apiEnabled) {
          // Show "limited options" notice
          this._showToast(this.ui.t.limitedOptions);
        }
      });
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 12: PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Public interface for the Mural Configurator.
   * 
   * Usage:
   *   MuralConfigurator.init('mural-configurator');
   * 
   * Or with a DOM element:
   *   MuralConfigurator.init(document.getElementById('my-container'));
   */
  const MuralConfigurator = {
    version: VERSION,

    /**
     * Initialize the configurator on a container element.
     * @param {string|HTMLElement} containerOrId - Element or element ID
     * @returns {MuralConfiguratorController|null}
     */
    init(containerOrId) {
      const container = typeof containerOrId === 'string'
        ? document.getElementById(containerOrId)
        : containerOrId;

      if (!container) {
        console.error('[MuralConfigurator] Container not found:', containerOrId);
        return null;
      }

      return new MuralConfiguratorController(container);
    },

    /**
     * Utility: format price for a given locale.
     */
    formatPrice,

    /**
     * Utility: snap width up to lane boundary.
     */
    snapWidthUp,
  };

  // Export
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = MuralConfigurator;
  } else {
    root.MuralConfigurator = MuralConfigurator;
  }

})(typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : this);
